'use client';

import type { FlagCatalogEntry } from '@/@feature-flags/catalog';
import type { CreateFeatureFlagRequest } from '@/@feature-flags/types/api';
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Switch,
    Textarea,
} from '@heroui/react';
import { useCallback, useEffect, useState } from 'react';

const KEY_PATTERN = /^[a-z0-9]+(?:[-.][a-z0-9]+)*$/;

interface CreateFlagModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payload: CreateFeatureFlagRequest) => Promise<unknown>;
    isSubmitting: boolean;
    /** Catalog keys that do not yet have a row. A key can still be typed. */
    suggestions: FlagCatalogEntry[];
    initialKey?: string;
    initialDescription?: string;
}

export function CreateFlagModal({
    isOpen,
    onClose,
    onSubmit,
    isSubmitting,
    suggestions,
    initialKey = '',
    initialDescription = '',
}: CreateFlagModalProps) {
    const [key, setKey] = useState('');
    const [description, setDescription] = useState('');
    const [descriptionEdited, setDescriptionEdited] = useState(false);
    const [enabledInternal, setEnabledInternal] = useState(false);
    const [enabledExternal, setEnabledExternal] = useState(false);
    const [keyError, setKeyError] = useState('');
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setKey(initialKey);
        setDescription(initialDescription);
        setDescriptionEdited(false);
        setEnabledInternal(false);
        setEnabledExternal(false);
        setKeyError('');
        setSubmitError('');
    }, [isOpen, initialKey, initialDescription]);

    const applyCatalogDescription = useCallback(
        (nextKey: string) => {
            if (descriptionEdited) return;
            const entry = suggestions.find((item) => item.key === nextKey);
            if (entry) setDescription(entry.description);
        },
        [descriptionEdited, suggestions],
    );

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleSubmit = useCallback(async () => {
        if (!key.trim()) {
            setKeyError('Key is required');
            return;
        }
        if (!KEY_PATTERN.test(key)) {
            setKeyError(
                'Lowercase alphanumeric with hyphens/dots (e.g. new-dashboard, beta.export)',
            );
            return;
        }
        setKeyError('');
        setSubmitError('');

        try {
            await onSubmit({
                key,
                description: description || undefined,
                enabledInternal,
                enabledExternal,
            });
            handleClose();
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to create feature flag');
        }
    }, [key, description, enabledInternal, enabledExternal, onSubmit, handleClose]);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg">
            <ModalContent>
                <ModalHeader>Create Feature Flag</ModalHeader>
                <ModalBody className="gap-4">
                    <Autocomplete
                        label="Key"
                        placeholder="Search a known key, or type a new one"
                        inputValue={key}
                        onInputChange={(value) => {
                            const next = value.toLowerCase();
                            setKey(next);
                            setKeyError('');
                            setSubmitError('');
                            applyCatalogDescription(next);
                        }}
                        selectedKey={suggestions.some((item) => item.key === key) ? key : null}
                        onSelectionChange={(selected) => {
                            if (selected == null) return;
                            const next = String(selected);
                            setKey(next);
                            setKeyError('');
                            setSubmitError('');
                            applyCatalogDescription(next);
                        }}
                        allowsCustomValue
                        isRequired
                        isInvalid={!!keyError}
                        errorMessage={keyError}
                        description="Lowercase alphanumeric with hyphens and dots"
                    >
                        {suggestions.map((item) => (
                            <AutocompleteItem
                                key={item.key}
                                textValue={item.key}
                                description={item.description}
                            >
                                {item.key}
                            </AutocompleteItem>
                        ))}
                    </Autocomplete>
                    <Textarea
                        label="Description"
                        placeholder="Human-readable description of this feature flag"
                        value={description}
                        onValueChange={(value) => {
                            setDescription(value);
                            setDescriptionEdited(true);
                            setSubmitError('');
                        }}
                    />
                    <div className="flex flex-col gap-3">
                        <Switch
                            isSelected={enabledInternal}
                            onValueChange={(value) => {
                                setEnabledInternal(value);
                                setSubmitError('');
                            }}
                            size="sm"
                        >
                            <span className="text-small">Enabled for Internal Users</span>
                        </Switch>
                        <Switch
                            isSelected={enabledExternal}
                            onValueChange={(value) => {
                                setEnabledExternal(value);
                                setSubmitError('');
                            }}
                            size="sm"
                        >
                            <span className="text-small">Enabled for External Users</span>
                        </Switch>
                    </div>
                    {submitError ? (
                        <p className="text-small text-danger" role="alert">
                            {submitError}
                        </p>
                    ) : null}
                </ModalBody>
                <ModalFooter>
                    <Button variant="flat" onPress={handleClose}>
                        Cancel
                    </Button>
                    <Button color="primary" onPress={handleSubmit} isLoading={isSubmitting}>
                        Create
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
